import React from "react";
import { Button, Form } from "react-bootstrap";
import { FiArrowDown, FiArrowUp, FiPlus, FiTrash2 } from "react-icons/fi";
import type { KpiGridFixedRow } from "../types/kpiEntryMode";

type Props = {
  rows: KpiGridFixedRow[];
  onChange: (next: KpiGridFixedRow[]) => void;
};

function renumber(rows: KpiGridFixedRow[]): KpiGridFixedRow[] {
  return rows.map((row, idx) => ({ ...row, list_order: idx }));
}

const KpiGridFixedRowsEditor: React.FC<Props> = ({ rows, onChange }) => {
  const addRow = () => {
    onChange(
      renumber([
        ...rows,
        {
          label: "",
          list_order: rows.length,
          is_active: true,
        },
      ])
    );
  };

  const updateRow = (index: number, patch: Partial<KpiGridFixedRow>) => {
    onChange(
      renumber(
        rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
      )
    );
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(renumber(next));
  };

  const removeOrDeactivate = (index: number) => {
    const row = rows[index];
    if (row.kpi_grid_fixed_row_id) {
      updateRow(index, { is_active: false });
      return;
    }
    onChange(renumber(rows.filter((_, i) => i !== index)));
  };

  return (
    <div className="mt-4" data-testid="kpi-grid-fixed-rows-editor">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold">Fixed rows</div>
        <Button
          variant="outline-primary"
          size="sm"
          className="btn-action"
          onClick={addRow}
          data-testid="kpi-grid-fixed-row-add"
        >
          <FiPlus className="me-1" />
          Add fixed row
        </Button>
      </div>
      <Form.Text className="text-muted d-block mb-2">
        Fixed-row labels are locked in the first column during entry. Min/max
        free-row limits do not include fixed rows.
      </Form.Text>

      {rows.length === 0 ? (
        <div className="text-muted small">No fixed rows.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {rows.map((row, index) => {
            const active = row.is_active !== false;
            return (
              <div
                key={row.kpi_grid_fixed_row_id ?? `new-fixed-${index}`}
                className={`d-flex gap-2 align-items-center border rounded px-2 py-2 ${
                  active ? "" : "opacity-50"
                }`}
                data-testid={`kpi-grid-fixed-row-${index}`}
              >
                <Form.Control
                  type="text"
                  placeholder="Fixed row label"
                  value={row.label}
                  disabled={!active}
                  onChange={(e) => updateRow(index, { label: e.target.value })}
                />
                {row.kpi_grid_fixed_row_id ? (
                  <Form.Check
                    type="switch"
                    id={`grid-fixed-active-${index}`}
                    label="Active"
                    checked={active}
                    onChange={(e) =>
                      updateRow(index, { is_active: e.target.checked })
                    }
                    className="flex-shrink-0"
                  />
                ) : null}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Move fixed row ${index + 1} up`}
                >
                  <FiArrowUp />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={index === rows.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Move fixed row ${index + 1} down`}
                >
                  <FiArrowDown />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ntcog-table-action-btn--danger flex-shrink-0"
                  onClick={() => removeOrDeactivate(index)}
                  aria-label={
                    row.kpi_grid_fixed_row_id
                      ? `Deactivate fixed row ${index + 1}`
                      : `Remove fixed row ${index + 1}`
                  }
                >
                  <FiTrash2 />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KpiGridFixedRowsEditor;
